import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import {
  SLDSLineChart, SLDSBarChart, SLDSDoughnutChart, SLDSRadarChart,
  SLDSScatterChart, SLDSPolarAreaChart, SparkLine,
  SLDS_CHART_COLORS, SLDS_CHART_COLORS_ALPHA
} from '../shared/ChartComponents';
import {
  generateTimeSeries, generateScatterData, generateHeatmapData,
  generateTeamPerformance
} from '../shared/SampleData';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import Button from '@salesforce/design-system-react/components/button';
import Modal from '@salesforce/design-system-react/components/modal';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const cardStyle = {
  backgroundColor: 'white',
  border: '1px solid #dddbda',
  borderRadius: '4px',
  padding: '20px',
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px',
};

const cardTitleStyle = {
  fontSize: '16px',
  fontWeight: '700',
  color: '#080707',
  margin: 0,
};

const cardDescStyle = {
  fontSize: '12px',
  color: '#706e6b',
  margin: '4px 0 0 0',
};

const gridTwoCol = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '20px',
  marginBottom: '20px',
};

const gridThreeCol = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '20px',
  marginBottom: '20px',
};

// ---------------------------------------------------------------------------
// ChartCard - reusable wrapper with fullscreen modal
// ---------------------------------------------------------------------------
const ChartCard = ({ title, description, children, height }) => {
  const [isModalOpen, setModalOpen] = useState(false);

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h3 style={cardTitleStyle}>{title}</h3>
          {description && <p style={cardDescStyle}>{description}</p>}
        </div>
        <Button
          iconCategory="utility"
          iconName="expand"
          iconSize="small"
          variant="icon"
          title="View fullscreen"
          onClick={() => setModalOpen(true)}
        />
      </div>
      <div>{children}</div>
      {isModalOpen && (
        <Modal
          isOpen
          onRequestClose={() => setModalOpen(false)}
          heading={title}
          size="large"
        >
          <div style={{ padding: '24px' }}>
            <div style={{ height: height || 500 }}>{children}</div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TAB 1 - Trends
// ---------------------------------------------------------------------------
const TrendsTab = () => {
  const monthLabels = useMemo(
    () => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    []
  );

  // Multi-series line chart data
  const lineData = useMemo(() => {
    const ts = generateTimeSeries(12, 3);
    return {
      labels: ts.labels,
      datasets: [
        {
          label: 'Revenue',
          data: ts.datasets[0].map(v => v * 5000),
          borderColor: SLDS_CHART_COLORS[0],
          backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Pipeline',
          data: ts.datasets[1].map(v => v * 7000),
          borderColor: SLDS_CHART_COLORS[1],
          backgroundColor: SLDS_CHART_COLORS_ALPHA[1],
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Bookings',
          data: ts.datasets[2].map(v => v * 3000),
          borderColor: SLDS_CHART_COLORS[2],
          backgroundColor: SLDS_CHART_COLORS_ALPHA[2],
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, []);

  // Stacked bar - quarterly revenue by product line
  const stackedBarData = useMemo(() => ({
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      { label: 'Enterprise Suite', data: [320, 410, 380, 450], backgroundColor: SLDS_CHART_COLORS[0] },
      { label: 'Pro License', data: [220, 260, 290, 310], backgroundColor: SLDS_CHART_COLORS[1] },
      { label: 'Analytics Add-on', data: [140, 170, 190, 220], backgroundColor: SLDS_CHART_COLORS[2] },
      { label: 'Support Package', data: [90, 110, 100, 130], backgroundColor: SLDS_CHART_COLORS[3] },
    ],
  }), []);

  // Grouped bar - monthly targets vs actuals
  const groupedBarData = useMemo(() => {
    const actuals = [180, 195, 210, 188, 225, 240, 260, 245, 275, 290, 310, 330];
    const targets = [200, 200, 210, 210, 220, 220, 250, 250, 260, 260, 300, 300];
    return {
      labels: monthLabels,
      datasets: [
        { label: 'Actual', data: actuals, backgroundColor: SLDS_CHART_COLORS[0], borderRadius: 3 },
        { label: 'Target', data: targets, backgroundColor: '#dddbda', borderRadius: 3 },
      ],
    };
  }, [monthLabels]);

  // SparkLine data
  const sparkMetrics = useMemo(() => [
    { label: 'MRR', value: '$1.24M', data: [80, 85, 82, 90, 88, 95, 100, 98, 105, 110, 115, 124], color: '#0176d3', change: '+8.2%' },
    { label: 'ARR', value: '$14.9M', data: [120, 122, 125, 128, 130, 132, 135, 138, 140, 142, 146, 149], color: '#2e844a', change: '+12.4%' },
    { label: 'Churn Rate', value: '2.1%', data: [35, 32, 30, 28, 31, 29, 27, 25, 24, 23, 22, 21], color: '#c23934', change: '-0.3%' },
    { label: 'NPS Score', value: '72', data: [60, 62, 58, 65, 63, 68, 70, 66, 69, 71, 73, 72], color: '#9050e9', change: '+5' },
    { label: 'CAC', value: '$2,450', data: [30, 32, 35, 33, 31, 29, 28, 27, 26, 25, 24, 24.5], color: '#fe9339', change: '-6.1%' },
    { label: 'LTV:CAC', value: '4.8x', data: [32, 35, 36, 38, 40, 39, 42, 43, 44, 45, 47, 48], color: '#06a59a', change: '+0.4x' },
  ], []);

  return (
    <div>
      <div style={gridTwoCol}>
        <ChartCard title="Revenue, Pipeline & Bookings" description="12-month multi-series trend with filled areas">
          <SLDSLineChart
            data={lineData}
            height={320}
            options={{
              scales: {
                y: {
                  ticks: {
                    callback: function(value) {
                      return '$' + (value / 1000).toFixed(0) + 'k';
                    },
                  },
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Revenue by Product Line" description="Quarterly stacked breakdown across four product lines">
          <SLDSBarChart
            data={stackedBarData}
            height={320}
            options={{
              scales: {
                x: { stacked: true },
                y: {
                  stacked: true,
                  ticks: {
                    callback: function(value) {
                      return '$' + value + 'k';
                    },
                  },
                },
              },
            }}
          />
        </ChartCard>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <ChartCard title="Monthly Targets vs Actuals" description="Grouped bar comparison of actual revenue against monthly targets">
          <SLDSBarChart
            data={groupedBarData}
            height={280}
            options={{
              scales: {
                y: {
                  ticks: {
                    callback: function(value) {
                      return '$' + value + 'k';
                    },
                  },
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* SparkLine grid */}
      <div style={{ ...gridThreeCol, gridTemplateColumns: 'repeat(6, 1fr)' }}>
        {sparkMetrics.map((metric) => (
          <div key={metric.label} style={cardStyle}>
            <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              {metric.label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#080707', marginBottom: '4px' }}>
              {metric.value}
            </div>
            <SparkLine data={metric.data} color={metric.color} width={120} height={32} />
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: metric.change.startsWith('-') && metric.label !== 'Churn Rate' && metric.label !== 'CAC' ? '#c23934' : '#2e844a',
              marginTop: '4px',
            }}>
              {metric.change}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TAB 2 - Composition
// ---------------------------------------------------------------------------
const CompositionTab = () => {
  // Doughnut - pipeline by stage
  const doughnutData = useMemo(() => ({
    labels: ['Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closing'],
    datasets: [{
      data: [980000, 720000, 540000, 410000, 280000],
      backgroundColor: SLDS_CHART_COLORS.slice(0, 5),
      borderWidth: 2,
      borderColor: 'white',
    }],
  }), []);

  const pipelineTotal = useMemo(
    () => doughnutData.datasets[0].data.reduce((a, b) => a + b, 0),
    [doughnutData]
  );

  // Polar area - revenue by region
  const polarData = useMemo(() => ({
    labels: ['North America', 'EMEA', 'APAC', 'LATAM', 'ANZ'],
    datasets: [{
      data: [420, 310, 260, 145, 95],
      backgroundColor: SLDS_CHART_COLORS_ALPHA.slice(0, 5),
      borderColor: SLDS_CHART_COLORS.slice(0, 5),
      borderWidth: 2,
    }],
  }), []);

  // 100% stacked bar - lead source mix
  const leadSourceData = useMemo(() => {
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const sources = ['Organic', 'Paid Search', 'Referral', 'Events', 'Direct'];
    const rawData = [
      [30, 25, 20, 15, 10],
      [28, 27, 18, 17, 10],
      [25, 28, 22, 14, 11],
      [22, 30, 24, 13, 11],
    ];
    return {
      labels: quarters,
      datasets: sources.map((source, si) => ({
        label: source,
        data: rawData.map(q => q[si]),
        backgroundColor: SLDS_CHART_COLORS[si],
      })),
    };
  }, []);

  // Funnel data
  const funnelStages = useMemo(() => [
    { label: 'Website Visitors', value: 45000, color: SLDS_CHART_COLORS[0] },
    { label: 'Leads Generated', value: 12400, color: SLDS_CHART_COLORS[1] },
    { label: 'MQLs', value: 6200, color: SLDS_CHART_COLORS[2] },
    { label: 'SQLs', value: 3100, color: SLDS_CHART_COLORS[3] },
    { label: 'Opportunities', value: 1550, color: SLDS_CHART_COLORS[4] },
    { label: 'Closed Won', value: 620, color: SLDS_CHART_COLORS[5] },
  ], []);

  const maxFunnelValue = funnelStages[0].value;

  return (
    <div>
      <div style={gridTwoCol}>
        <ChartCard title="Pipeline by Stage" description="Interactive doughnut showing total pipeline distribution">
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ flex: '1 1 55%' }}>
              <SLDSDoughnutChart
                data={doughnutData}
                height={320}
                options={{
                  cutout: '60%',
                  plugins: { legend: { display: false } },
                }}
              />
            </div>
            <div style={{ flex: '1 1 45%' }}>
              <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Total Pipeline
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#080707', marginBottom: '16px' }}>
                ${(pipelineTotal / 1000000).toFixed(1)}M
              </div>
              {doughnutData.labels.map((label, i) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f3f3' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: SLDS_CHART_COLORS[i] }} />
                    <span style={{ fontSize: '13px', color: '#3e3e3c' }}>{label}</span>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#080707' }}>
                    ${(doughnutData.datasets[0].data[i] / 1000).toFixed(0)}k
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Revenue by Region" description="Polar area chart comparing regional revenue contribution">
          <SLDSPolarAreaChart data={polarData} height={320} />
        </ChartCard>
      </div>

      <div style={gridTwoCol}>
        <ChartCard title="Lead Source Mix Over Time" description="100% stacked bar showing quarterly lead source proportions">
          <SLDSBarChart
            data={leadSourceData}
            height={320}
            options={{
              scales: {
                x: { stacked: true },
                y: {
                  stacked: true,
                  max: 100,
                  ticks: {
                    callback: function(value) {
                      return value + '%';
                    },
                  },
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Sales Funnel" description="Conversion funnel from website visitors to closed deals">
          <div style={{ padding: '12px 0' }}>
            {funnelStages.map((stage, idx) => {
              const widthPct = (stage.value / maxFunnelValue) * 100;
              const conversionRate = idx > 0 ? ((stage.value / funnelStages[idx - 1].value) * 100).toFixed(1) : null;
              return (
                <div key={stage.label} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ width: '120px', fontSize: '12px', color: '#3e3e3c', textAlign: 'right', paddingRight: '12px', flexShrink: 0 }}>
                    {stage.label}
                  </div>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <div
                      style={{
                        width: `${widthPct}%`,
                        minWidth: '40px',
                        height: '36px',
                        backgroundColor: stage.color,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'width 0.3s ease',
                      }}
                    >
                      {stage.value.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ width: '60px', fontSize: '11px', color: '#706e6b', paddingLeft: '8px', flexShrink: 0 }}>
                    {conversionRate ? `${conversionRate}%` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TAB 3 - Relationships
// ---------------------------------------------------------------------------
const RelationshipsTab = () => {
  // Scatter - deal size vs days to close
  const scatterRaw = useMemo(() => generateScatterData(60), []);

  const scatterData = useMemo(() => ({
    datasets: [
      {
        label: 'Won',
        data: scatterRaw.filter(d => d.won).map(d => ({ x: d.x, y: d.y })),
        backgroundColor: SLDS_CHART_COLORS[1] + 'AA',
        borderColor: SLDS_CHART_COLORS[1],
        borderWidth: 1,
        pointRadius: 5,
      },
      {
        label: 'Lost',
        data: scatterRaw.filter(d => !d.won).map(d => ({ x: d.x, y: d.y })),
        backgroundColor: SLDS_CHART_COLORS[3] + 'AA',
        borderColor: SLDS_CHART_COLORS[3],
        borderWidth: 1,
        pointRadius: 5,
      },
    ],
  }), [scatterRaw]);

  // Bubble chart - accounts by revenue/employees/growth
  const bubbleData = useMemo(() => ({
    datasets: [
      {
        label: 'High Growth',
        data: [
          { x: 1200000, y: 450, r: 18 },
          { x: 800000, y: 320, r: 14 },
          { x: 2100000, y: 820, r: 22 },
          { x: 600000, y: 150, r: 10 },
          { x: 1500000, y: 600, r: 16 },
        ],
        backgroundColor: SLDS_CHART_COLORS[0] + '88',
        borderColor: SLDS_CHART_COLORS[0],
        borderWidth: 1,
      },
      {
        label: 'Moderate Growth',
        data: [
          { x: 900000, y: 280, r: 12 },
          { x: 1800000, y: 700, r: 15 },
          { x: 400000, y: 200, r: 8 },
          { x: 1100000, y: 500, r: 11 },
          { x: 700000, y: 350, r: 9 },
        ],
        backgroundColor: SLDS_CHART_COLORS[2] + '88',
        borderColor: SLDS_CHART_COLORS[2],
        borderWidth: 1,
      },
      {
        label: 'Low Growth',
        data: [
          { x: 500000, y: 400, r: 7 },
          { x: 300000, y: 180, r: 6 },
          { x: 1400000, y: 900, r: 10 },
          { x: 250000, y: 120, r: 5 },
        ],
        backgroundColor: SLDS_CHART_COLORS[3] + '88',
        borderColor: SLDS_CHART_COLORS[3],
        borderWidth: 1,
      },
    ],
  }), []);

  // Radar - team member comparison
  const teamPerf = useMemo(() => generateTeamPerformance(), []);

  const radarData = useMemo(() => ({
    labels: teamPerf.labels,
    datasets: teamPerf.datasets.slice(0, 3).map((member, i) => ({
      label: member.name,
      data: member.values,
      borderColor: SLDS_CHART_COLORS[i],
      backgroundColor: SLDS_CHART_COLORS_ALPHA[i],
      borderWidth: 2,
      pointBackgroundColor: SLDS_CHART_COLORS[i],
      pointRadius: 3,
    })),
  }), [teamPerf]);

  // Heatmap data
  const heatmapRaw = useMemo(() => generateHeatmapData(), []);

  // Color intensity helper
  const heatColor = (value) => {
    if (value < 20) return '#f3f3f3';
    if (value < 40) return SLDS_CHART_COLORS_ALPHA[0];
    if (value < 60) return '#0176d366';
    if (value < 80) return '#0176d3AA';
    return '#0176d3';
  };

  const cellSize = 44;
  const labelWidth = 40;
  const headerHeight = 24;
  const heatmapWidth = labelWidth + heatmapRaw.hours.length * cellSize;
  const heatmapHeight = headerHeight + heatmapRaw.days.length * cellSize;

  return (
    <div>
      <div style={gridTwoCol}>
        <ChartCard title="Deal Size vs Days to Close" description="Scatter plot colored by win/loss outcome">
          <SLDSScatterChart
            data={scatterData}
            height={340}
            options={{
              scales: {
                x: {
                  title: { display: true, text: 'Days to Close', font: { size: 12 }, color: '#706e6b' },
                },
                y: {
                  title: { display: true, text: 'Deal Size ($)', font: { size: 12 }, color: '#706e6b' },
                  ticks: {
                    callback: function(value) {
                      return '$' + (value / 1000).toFixed(0) + 'k';
                    },
                  },
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Account Landscape" description="Bubble chart: x=Revenue, y=Employees, size=Growth rate">
          <SLDSScatterChart
            data={bubbleData}
            height={340}
            options={{
              scales: {
                x: {
                  title: { display: true, text: 'Annual Revenue ($)', font: { size: 12 }, color: '#706e6b' },
                  ticks: {
                    callback: function(value) {
                      return '$' + (value / 1000000).toFixed(1) + 'M';
                    },
                  },
                },
                y: {
                  title: { display: true, text: 'Employees', font: { size: 12 }, color: '#706e6b' },
                },
              },
            }}
          />
        </ChartCard>
      </div>

      <div style={gridTwoCol}>
        <ChartCard title="Team Performance Comparison" description="Radar chart comparing three reps across five key metrics">
          <SLDSRadarChart
            data={radarData}
            height={360}
            options={{
              scales: {
                r: {
                  min: 0,
                  max: 100,
                  ticks: { stepSize: 20 },
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Activity Heatmap" description="Activity volume by day of week and hour of day">
          <div style={{ overflowX: 'auto', padding: '8px 0' }}>
            <svg width={heatmapWidth} height={heatmapHeight + 40} style={{ display: 'block', margin: '0 auto' }}>
              {/* Hour headers */}
              {heatmapRaw.hours.map((hour, hi) => (
                <text
                  key={`h-${hi}`}
                  x={labelWidth + hi * cellSize + cellSize / 2}
                  y={headerHeight - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#706e6b"
                  fontFamily="'Salesforce Sans', Arial, sans-serif"
                >
                  {hour}
                </text>
              ))}

              {/* Day labels + cells */}
              {heatmapRaw.days.map((day, di) => (
                <g key={`d-${di}`}>
                  <text
                    x={labelWidth - 8}
                    y={headerHeight + di * cellSize + cellSize / 2 + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#3e3e3c"
                    fontFamily="'Salesforce Sans', Arial, sans-serif"
                  >
                    {day}
                  </text>
                  {heatmapRaw.hours.map((_, hi) => (
                    <rect
                      key={`c-${di}-${hi}`}
                      x={labelWidth + hi * cellSize}
                      y={headerHeight + di * cellSize}
                      width={cellSize - 3}
                      height={cellSize - 3}
                      rx={3}
                      fill={heatColor(heatmapRaw.values[di][hi])}
                      stroke="#ffffff"
                      strokeWidth={1}
                    >
                      <title>{`${day} ${heatmapRaw.hours[hi]}: ${heatmapRaw.values[di][hi]} activities`}</title>
                    </rect>
                  ))}
                </g>
              ))}

              {/* Legend */}
              <g transform={`translate(${labelWidth}, ${heatmapHeight + 12})`}>
                <text fontSize="10" fill="#706e6b" y={10} fontFamily="'Salesforce Sans', Arial, sans-serif">Less</text>
                {[0, 20, 40, 60, 80].map((v, i) => (
                  <rect key={`leg-${i}`} x={32 + i * 22} y={0} width={18} height={14} rx={2} fill={heatColor(v + 10)} />
                ))}
                <text fontSize="10" fill="#706e6b" x={32 + 5 * 22 + 4} y={10} fontFamily="'Salesforce Sans', Arial, sans-serif">More</text>
              </g>
            </svg>
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TAB 4 - Gauges & Special
// ---------------------------------------------------------------------------
const GaugesSpecialTab = () => {
  // Gauge data
  const gauges = useMemo(() => [
    { label: 'Quota Attainment', value: 78, target: 100, unit: '%', color: '#0176d3' },
    { label: 'Customer Satisfaction', value: 92, target: 100, unit: '%', color: '#2e844a' },
    { label: 'Pipeline Coverage', value: 3.2, target: 5, unit: 'x', color: '#fe9339' },
  ], []);

  // Bullet chart data
  const bulletMetrics = useMemo(() => [
    { label: 'Revenue', actual: 820, target: 900, range: [0, 600, 750, 1000], color: SLDS_CHART_COLORS[0] },
    { label: 'New Logos', actual: 42, target: 50, range: [0, 30, 40, 60], color: SLDS_CHART_COLORS[1] },
    { label: 'Retention', actual: 94, target: 95, range: [0, 80, 90, 100], color: SLDS_CHART_COLORS[4] },
    { label: 'NPS', actual: 72, target: 80, range: [0, 50, 65, 100], color: SLDS_CHART_COLORS[5] },
  ], []);

  // Waterfall chart data
  const waterfallData = useMemo(() => {
    const segments = [
      { label: 'Starting ARR', value: 12000, type: 'total' },
      { label: 'New Business', value: 2400, type: 'increase' },
      { label: 'Expansion', value: 1800, type: 'increase' },
      { label: 'Churn', value: -1100, type: 'decrease' },
      { label: 'Contraction', value: -600, type: 'decrease' },
      { label: 'Ending ARR', value: 14500, type: 'total' },
    ];

    // Calculate floating bar positions
    let running = 0;
    const bars = segments.map((seg) => {
      if (seg.type === 'total') {
        const bar = { ...seg, start: 0, end: seg.value };
        running = seg.value;
        return bar;
      }
      const start = running;
      running = running + seg.value;
      return { ...seg, start: Math.min(start, running), end: Math.max(start, running) };
    });

    return {
      labels: bars.map(b => b.label),
      datasets: [
        {
          label: 'Invisible base',
          data: bars.map(b => b.start),
          backgroundColor: 'transparent',
          borderWidth: 0,
          barPercentage: 0.6,
        },
        {
          label: 'Value',
          data: bars.map(b => b.end - b.start),
          backgroundColor: segments.map(s =>
            s.type === 'total' ? '#0176d3' : s.type === 'increase' ? '#2e844a' : '#c23934'
          ),
          borderRadius: 3,
          barPercentage: 0.6,
        },
      ],
    };
  }, []);

  return (
    <div>
      {/* Gauge row */}
      <div style={gridThreeCol}>
        {gauges.map((gauge) => {
          const pct = gauge.target === 100 ? gauge.value / 100 : gauge.value / gauge.target;
          return (
            <ChartCard key={gauge.label} title={gauge.label}>
              <div style={{ textAlign: 'center' }}>
                <SLDSDoughnutChart
                  data={{
                    datasets: [{
                      data: [pct * 100, 100 - pct * 100],
                      backgroundColor: [gauge.color, '#f3f3f3'],
                      borderWidth: 0,
                      circumference: 180,
                      rotation: 270,
                    }],
                  }}
                  height={180}
                  options={{
                    cutout: '75%',
                    plugins: { legend: { display: false }, tooltip: { enabled: false } },
                  }}
                />
                <div style={{ marginTop: '-60px', position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#080707' }}>
                    {gauge.value}{gauge.unit}
                  </div>
                  <div style={{ fontSize: '12px', color: '#706e6b' }}>
                    Target: {gauge.target}{gauge.unit}
                  </div>
                </div>
              </div>
            </ChartCard>
          );
        })}
      </div>

      <div style={gridTwoCol}>
        {/* Bullet Charts */}
        <ChartCard title="Bullet Charts" description="Performance against target with qualitative ranges">
          <div style={{ padding: '8px 0' }}>
            {bulletMetrics.map((metric) => {
              const max = metric.range[metric.range.length - 1];
              const pctActual = (metric.actual / max) * 100;
              const pctTarget = (metric.target / max) * 100;
              const pctRange1 = (metric.range[1] / max) * 100;
              const pctRange2 = (metric.range[2] / max) * 100;
              return (
                <div key={metric.label} style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c' }}>{metric.label}</span>
                    <span style={{ fontSize: '12px', color: '#706e6b' }}>
                      {metric.actual} / {metric.target}
                    </span>
                  </div>
                  <div style={{ position: 'relative', height: '28px', backgroundColor: '#f3f3f3', borderRadius: '4px', overflow: 'hidden' }}>
                    {/* Qualitative ranges */}
                    <div style={{
                      position: 'absolute', top: 0, left: 0, height: '100%',
                      width: `${pctRange1}%`, backgroundColor: '#e0e0e0',
                    }} />
                    <div style={{
                      position: 'absolute', top: 0, left: `${pctRange1}%`, height: '100%',
                      width: `${pctRange2 - pctRange1}%`, backgroundColor: '#d0d0d0',
                    }} />
                    {/* Actual bar */}
                    <div style={{
                      position: 'absolute', top: '6px', left: 0, height: '16px',
                      width: `${pctActual}%`, backgroundColor: metric.color,
                      borderRadius: '3px', zIndex: 2,
                    }} />
                    {/* Target marker */}
                    <div style={{
                      position: 'absolute', top: '2px', left: `${pctTarget}%`,
                      width: '3px', height: '24px', backgroundColor: '#080707',
                      borderRadius: '1px', zIndex: 3,
                      transform: 'translateX(-1.5px)',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>

        {/* Waterfall Chart */}
        <ChartCard title="Revenue Bridge" description="Waterfall chart: Starting ARR through changes to Ending ARR">
          <SLDSBarChart
            data={waterfallData}
            height={320}
            options={{
              scales: {
                x: { stacked: true },
                y: {
                  stacked: true,
                  ticks: {
                    callback: function(value) {
                      return '$' + (value / 1000).toFixed(0) + 'k';
                    },
                  },
                },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  filter: function(tooltipItem) {
                    return tooltipItem.datasetIndex === 1;
                  },
                  callbacks: {
                    label: function(context) {
                      return '$' + ((context.raw * 1000) / 1000).toLocaleString() + 'k';
                    },
                  },
                },
              },
            }}
          />
        </ChartCard>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const AnalyticsSuite = () => {
  return (
    <ShowcasePageWrapper
      title="Analytics Suite"
      description="Every chart type and visualization pattern"
      icon="analytics"
      iconCategory="standard"
    >
      <Tabs id="analytics-suite-tabs">
        <TabsPanel label="Trends">
          <div style={{ padding: '20px 0' }}>
            <TrendsTab />
          </div>
        </TabsPanel>
        <TabsPanel label="Composition">
          <div style={{ padding: '20px 0' }}>
            <CompositionTab />
          </div>
        </TabsPanel>
        <TabsPanel label="Relationships">
          <div style={{ padding: '20px 0' }}>
            <RelationshipsTab />
          </div>
        </TabsPanel>
        <TabsPanel label="Gauges & Special">
          <div style={{ padding: '20px 0' }}>
            <GaugesSpecialTab />
          </div>
        </TabsPanel>
      </Tabs>
    </ShowcasePageWrapper>
  );
};

export default AnalyticsSuite;
